/**
 * Pointer-event drag and resize for FloatingPanel (mouse, pen and touch).
 * Listeners live on `document` for the duration of one gesture, mirroring the
 * legacy map window, so a fast pointer that leaves the header keeps dragging.
 */
import { useCallback, useEffect, useRef } from "react";

import { dragPosition, resizeSize } from "./floatingGeometry";

// A press that starts on a control inside the header is a click, not a drag.
const NO_DRAG_SELECTOR = "button, input, select, textarea, a[href], [data-no-drag]";

/**
 * @param {object} args
 * @param {{current: HTMLElement|null}} args.frameRef
 * @param {{width:number,height:number}} args.minSize
 * @param {(pos:{x:number,y:number}, size:{width:number,height:number}) => void} args.onDrag
 * @param {(size:{width:number,height:number}) => void} args.onResize
 * @param {(active:boolean) => void} [args.onInteractionChange]
 */
export function usePanelPointer({ frameRef, minSize, onDrag, onResize, onInteractionChange }) {
  const handlersRef = useRef({ minSize, onDrag, onResize, onInteractionChange });
  handlersRef.current = { minSize, onDrag, onResize, onInteractionChange };
  const endGestureRef = useRef(null);

  useEffect(() => () => { if (endGestureRef.current) endGestureRef.current(); }, []);

  const track = useCallback((startEvent, apply) => {
    if (endGestureRef.current) endGestureRef.current();
    const startX = startEvent.clientX;
    const startY = startEvent.clientY;
    const body = document.body;
    const prevUserSelect = body.style.userSelect;
    body.style.userSelect = "none";
    handlersRef.current.onInteractionChange?.(true);

    const onMove = (ev) => apply(ev.clientX - startX, ev.clientY - startY);
    const end = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      body.style.userSelect = prevUserSelect;
      endGestureRef.current = null;
      handlersRef.current.onInteractionChange?.(false);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
    endGestureRef.current = end;
  }, []);

  const startDrag = useCallback((e) => {
    if (e.isPrimary === false) return;
    if (e.target?.closest?.(NO_DRAG_SELECTOR)) return;
    const el = frameRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const origin = { x: rect.left, y: rect.top };
    const size = { width: rect.width, height: rect.height };
    track(e, (dx, dy) => handlersRef.current.onDrag(dragPosition(origin, dx, dy), size));
  }, [frameRef, track]);

  const startResize = useCallback((e, direction) => {
    if (e.isPrimary === false) return;
    const el = frameRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    const origin = { width: rect.width, height: rect.height };
    track(e, (dx, dy) => handlersRef.current.onResize(
      resizeSize(origin, direction, dx, dy, handlersRef.current.minSize),
    ));
  }, [frameRef, track]);

  return { startDrag, startResize };
}
